<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import { computed, onMounted, ref, watch } from 'vue';

import IconChevron from '@/shared/components/UI/Icons/IconChevron.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { breakpoints } from '@/shared/constants/constants';

const transitionTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

const props = defineProps<{
  productsCount: number;
}>();

const { t } = useTranslations();

const DEFAULT_MAX_HEIGHT = '600px';
const IS_COLLAPSED_THRESHOLD = 5;
const isCollapsed = ref(props.productsCount > IS_COLLAPSED_THRESHOLD);
const containerRef = ref<HTMLElement | null>(null);
const maxHeight = ref<string>('');

const toggleCollapse = () => {
  isCollapsed.value = !isCollapsed.value;
};

const setExpandedHeight = () => {
  if (!containerRef.value) return;
  maxHeight.value = `${containerRef.value.scrollHeight}px`;
};
const { width } = useWindowSize();
const scrollThreshold = computed(() => {
  return width.value < breakpoints.md ? 150 : 100; // Because we have a sticky header on mobile, we need to scroll a bit more to get to the top of the container
});

const scrollToCollapsedTop = () => {
  if (!containerRef.value) return;
  const targetTop =
    window.scrollY +
    containerRef.value.getBoundingClientRect().top -
    scrollThreshold.value;
  window.scrollTo({ top: targetTop, behavior: 'smooth' });
};

onMounted(() => {
  if (isCollapsed.value) {
    maxHeight.value = DEFAULT_MAX_HEIGHT;
    return;
  }
  maxHeight.value = '';
});

watch(
  isCollapsed,
  async (collapsed) => {
    if (!containerRef.value) return;

    if (transitionTimeout.value) {
      clearTimeout(transitionTimeout.value);
      transitionTimeout.value = null;
    }

    if (collapsed) {
      maxHeight.value = `${containerRef.value.scrollHeight}px`;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          maxHeight.value = DEFAULT_MAX_HEIGHT;
          scrollToCollapsedTop();
        });
      });
    } else {
      setExpandedHeight();
      transitionTimeout.value = setTimeout(() => {
        maxHeight.value = '';
      }, 300);
    }
  },
  { flush: 'post' }
);
</script>
<template>
  <div
    ref="containerRef"
    class="overflow-hidden transition-[max-height] duration-300 ease-in-out"
    :style="{ maxHeight }"
  >
    <slot name="default"></slot>
  </div>
  <div
    class="relative -mx-2 flex flex-col gap-2 rounded-b-lg border-t border-gray-200 bg-white p-4 before:pointer-events-none"
    :class="[
      isCollapsed
        ? 'sticky bottom-0 before:absolute before:-top-2 before:left-0 before:-z-1 before:block before:h-2 before:w-full before:bg-[radial-gradient(at_50%_100%,rgba(0,0,0,0.2),transparent_75%)] before:bg-[length:100%_8px] before:content-[\'\']'
        : 'relative'
    ]"
  >
    <button
      v-if="productsCount && productsCount > IS_COLLAPSED_THRESHOLD"
      class="absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-1/2 items-center gap-0.5 rounded-xl border border-gray-200 bg-white px-4 py-1 text-xs text-black shadow-md transition-colors duration-150 hover:cursor-pointer hover:border-gray-300"
      @click="toggleCollapse"
    >
      <span v-if="isCollapsed">
        {{ t('patterns.products.show-all-products', { productsCount }) }}
      </span>
      <span v-else>{{ t('patterns.products.show-less') }}</span>

      <IconChevron class="text-lg" :direction="isCollapsed ? 'down' : 'up'" />
    </button>

    <slot name="footer"></slot>
  </div>
</template>
